import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, QrCode, BarChart2, BookOpen, FileText, User, LogOut, CreditCard, Crown, Users, Megaphone, Layers, Sparkles } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { supabase } from '../lib/supabase';
import { Logo } from './Logo';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccessStatistics, canAccessReports, checkFeatureAccessAndRedirect } = usePlanLimits();

  const menuItems = [
    { path: '/qr/dash', label: 'Dashboard', icon: Home, requiresPremium: false },
    { path: '/qr/dash/links', label: 'QR-Codes', icon: QrCode, requiresPremium: false },
    { path: '/qr/dash/content', label: 'Inhalte', icon: Layers, requiresPremium: false, sublabel: 'Bücher / Kampagnen' },
    { path: '/qr/dash/ai-ideas', label: 'KI Ideen', icon: Sparkles, requiresPremium: false },
    { path: '/qr/dash/stats', label: 'Statistiken', icon: BarChart2, requiresPremium: true, canAccess: canAccessStatistics },
    { path: '/qr/dash/reports', label: 'Berichte', icon: FileText, requiresPremium: true, canAccess: canAccessReports },
    { path: '/qr/dash/team', label: 'Team', icon: Users, requiresPremium: false },
    { path: '/qr/dash/profile', label: 'Profil', icon: User, requiresPremium: false },
  ];

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/qr/login');
  };

  const handlePremiumFeatureClick = (e: React.MouseEvent, feature: 'statistics' | 'reports') => {
    e.preventDefault();
    checkFeatureAccessAndRedirect(feature);
  };

  return (
    <div className="w-64 bg-white shadow-lg border-r border-gray-200 min-h-screen fixed left-0 top-0 flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200">
        <Link to="/qr/dash" className="flex items-center space-x-3">
          <Logo size={40} />
          <span className="text-xl font-bold text-gray-900">BeyondPages</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const hasAccess = !item.requiresPremium || (item.canAccess && item.canAccess());
          
          if (item.requiresPremium && !hasAccess) {
            // Premium feature without access - show as disabled with premium indicator
            return (
              <button
                key={item.path}
                onClick={(e) => {
                  if (item.path.includes('stats')) {
                    handlePremiumFeatureClick(e, 'statistics');
                  } else if (item.path.includes('reports')) {
                    handlePremiumFeatureClick(e, 'reports');
                  }
                }}
                className="flex items-center justify-between w-full px-4 py-3 rounded-lg mb-2 transition-colors text-gray-400 hover:bg-gray-50 cursor-pointer"
                title={`${item.label} ist nur in kostenpflichtigen Plänen verfügbar`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="w-5 h-5" />
                  <div>
                    <span className="font-medium">{item.label}</span>
                    {item.sublabel && <span className="text-xs text-gray-500 block">{item.sublabel}</span>}
                  </div>
                </div>
                <Crown className="w-4 h-4 text-yellow-500" />
              </button>
            );
          }
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                isActive
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <Icon className="w-5 h-5" />
              <div>
                <span className="font-medium">{item.label}</span>
                {item.sublabel && <span className="text-xs text-gray-500 block">{item.sublabel}</span>}
              </div>
            </Link>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-200">
        {user && (
          <div className="mb-4 px-4 py-2 text-sm text-gray-600">
            {user.email}
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-700 transition-colors w-full"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Abmelden</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar; 